import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {
  CodonOptimizationMethod,
  GCContent,
  GC_LEVEL_NAME,
  GCLevel,
  HairpinParameter,
  Organism,
} from 'molbio/codon-optimization/CodonOptimizationConstants';
import CodonOptimizationHairpinParameters from 'molbio/codon-optimization/CodonOptimizationHairpinParameters';
import {getGCContentError} from 'molbio/codon-optimization/CodonOptimizationUtil';
import OrganismSelector from 'molbio/OrganismSelector';
import {FONT_PROXIMA_TITLE, FONT_PROXIMA_11} from 'styles/FontConstants';
import {ButtonSize} from 'ui-library/buttons/Button';
import {GRAY_DARKER} from 'ui-library/Colors';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import FieldError from 'ui-library/FieldError';
import Popover from 'ui-library/floating-ui/Popover';
import HelpIcon from 'ui-library/icons/HelpIcon';
import CheckboxInput from 'ui-library/inputs/CheckboxInput';
import {IntermediateValueControlledNumericInput} from 'ui-library/inputs/ControlledNumericInput';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import ToggleGroup from 'ui-library/ToggleGroup';
import {InputChangeEvent, NumericInputChangeEvent} from 'util/Types';

interface CodonOptimizationTabParametersProps {
  bases?: string;
  selectedOrganism: Organism | null;
  selectedGCLevel: GCLevel;
  onUpdateOrganism: (value: Organism) => void;
  onUpdateGCLevel: (value: GCLevel) => void;
  avoidHairpins: boolean;
  hairpinStem: HairpinParameter;
  hairpinWindow: HairpinParameter;
  uridineDepletion: boolean;
  onUpdateAvoidHairpins: (checked: boolean) => void;
  onUpdateHairpinStem: (stem: string) => void;
  onUpdateHairpinWindow: (window: string) => void;
  onUpdateUridineDepletion: (e: InputChangeEvent) => void;
  method: CodonOptimizationMethod;
  onUpdateMethod: (method: CodonOptimizationMethod) => void;
  gcContent: GCContent;
  onUpdateGCContent: (content: GCContent) => void;
}

const gcContentLevels = Object.keys(GC_LEVEL_NAME) as Array<GCLevel>;

const CodonOptimizationTabParameters = (props: CodonOptimizationTabParametersProps): JSX.Element => (
  <div className={css(styles.container)}>
    <div className={css(styles.title)}>Parameters</div>
    <div className={css(styles.paramSection)}>
      <div className={css(styles.organismSection)}>
        <FieldLabel label="Organism" />
        <OrganismSelector onChange={props.onUpdateOrganism} selected={props.selectedOrganism} />
        <p className={css(styles.explanationText)}>Only supports standard genetic code</p>
      </div>
    </div>
    {props.selectedOrganism != null && (
      <div className={css(styles.paramSection)}>
        <div className={css(styles.methodLabel)}>
          <FieldLabel label="Method" />
          <Popover
            renderContent={() => (
              <>
                These algorithms have been implemented using{' '}
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  href="https://edinburgh-genome-foundry.github.io/DnaChisel/"
                >
                  DNAChisel
                </a>
                .
              </>
            )}
          >
            <HelpIcon />
          </Popover>
        </div>
        <ToggleGroup
          selection={props.method}
          onChangeSelection={props.onUpdateMethod}
          size={ButtonSize.MEDIUM}
          isFullWidth={true}
          options={[
            {
              id: CodonOptimizationMethod.MATCH_CODON_USAGE,
              buttonProps: {
                text: (
                  <span className={css(styles.methodOption)}>
                    <p className={css(styles.methodOptionTitle)}>Match codon usage</p>
                    <p className={css(styles.methodOptionDesc)}>
                      The final sequence's codon usage will match to the greatest extent possible the codon
                      usage profile of the target species
                    </p>
                  </span>
                ),
              },
            },
            {
              id: CodonOptimizationMethod.USE_BEST_CODON,
              buttonProps: {
                text: (
                  <span className={css(styles.methodOption)}>
                    <p className={css(styles.methodOptionTitle)}>Use best codon</p>
                    <p className={css(styles.methodOptionDesc)}>
                      Every codon will be replaced by the “best” (i.e. most frequent) synonymous codon in the
                      target organism
                    </p>
                  </span>
                ),
              },
            },
          ]}
          wrap={true}
        />
      </div>
    )}

    <div className={css(styles.paramSection)}>
      <div className={css(styles.gcContent)}>
        <div className={css(styles.gcDropdown)}>
          <FieldLabel label="GC content" />
          <SelectDropdown
            menu={
              <Menu height={Menu.HEIGHTS.MEDIUM} width={Menu.WIDTHS.MEDIUM} value={props.selectedGCLevel}>
                {gcContentLevels.map((level) => (
                  <Option
                    key={level}
                    value={level}
                    onSelect={props.onUpdateGCLevel}
                    dataTestAttrs={{
                      component: 'CodonOptimizationTabParameters',
                      element: 'GCContentOption',
                      key: level,
                    }}
                  >
                    {GC_LEVEL_NAME[level]}
                  </Option>
                ))}
              </Menu>
            }
            dataTestAttrs={{component: 'CodonOptimizationTabParameters', element: 'GCContentSelector'}}
            displayName={GC_LEVEL_NAME[props.selectedGCLevel]}
          />
        </div>
        <div className={css(styles.paramInputs)}>
          {props.selectedGCLevel === GCLevel.CUSTOM && (
            <>
              <div>
                <p className={css(styles.paramInputLabel)}>Minimum</p>
                <IntermediateValueControlledNumericInput
                  min={0}
                  max={1}
                  onChange={(e: NumericInputChangeEvent) => {
                    props.onUpdateGCContent({min: e.target.numericValue, max: props.gcContent.max});
                  }}
                  value={props.gcContent.min}
                  validateInteger={false}
                  required={true}
                />
              </div>
              <div>
                <p className={css(styles.paramInputLabel)}>Maximum</p>
                <IntermediateValueControlledNumericInput
                  min={0}
                  max={1}
                  onChange={(e: NumericInputChangeEvent) => {
                    props.onUpdateGCContent({min: props.gcContent.min, max: e.target.numericValue});
                  }}
                  value={props.gcContent.max}
                  validateInteger={false}
                  required={true}
                />
              </div>
            </>
          )}
        </div>
      </div>
      <FieldError
        message={props.selectedGCLevel === GCLevel.CUSTOM ? getGCContentError(props.gcContent) : null}
      />
    </div>

    <div className={css(styles.paramSection)}>
      <CheckboxInput
        checked={props.uridineDepletion}
        id="uridineDepletion"
        label={<FieldLabel label="mRNA uridine depletion" />}
        disabled={false}
        onChange={props.onUpdateUridineDepletion}
        dataTestAttrs={{
          component: 'CodonOptimizationTabParameters',
          element: 'uridineDepletionCheckbox',
        }}
      />
      <p className={css(styles.explanationText, styles.urididineDesc)}>
        Depleting uridine in transcribed mRNA has been implicated in reduced immunogenicity
      </p>
    </div>

    <div className={css(styles.paramSection)}>
      <CodonOptimizationHairpinParameters
        bases={props.bases}
        avoidHairpins={props.avoidHairpins}
        hairpinStem={props.hairpinStem}
        hairpinWindow={props.hairpinWindow}
        onUpdateAvoidHairpins={props.onUpdateAvoidHairpins}
        onUpdateHairpinStem={props.onUpdateHairpinStem}
        onUpdateHairpinWindow={props.onUpdateHairpinWindow}
      />
    </div>
  </div>
);

const styles = StyleSheet.create({
  container: {
    marginBottom: 24,
  },
  title: {
    ...FONT_PROXIMA_TITLE,
    margin: '8px 0',
    textTransform: 'uppercase',
    color: Color.GRAY_600,
  },
  paramSection: {
    marginBottom: 16,
    maxWidth: 800,
  },
  organismSection: {
    width: '50%',
  },
  methodLabel: {
    display: 'flex',
    gap: 4,
  },
  methodOption: {
    textAlign: 'left',
  },
  methodOptionTitle: {
    ...FONT_PROXIMA_TITLE,
    marginBottom: 8,
  },
  methodOptionDesc: {
    ...FONT_PROXIMA_11,
    marginBottom: 0,
  },
  gcContent: {
    display: 'flex',
    gap: 8,
  },
  gcDropdown: {
    flex: 1,
  },
  paramInputs: {
    display: 'flex',
    gap: 8,
    flex: 1,
  },
  paramInputLabel: {
    marginBottom: 4,
  },
  urididineDesc: {
    marginLeft: 20,
  },
  explanationText: {
    color: GRAY_DARKER,
  },
});

export default CodonOptimizationTabParameters;
